import { useState } from 'react';
import { DndContext, DragEndEvent, closestCorners, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { DroppableColumn } from './DroppableColumn';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useMobile } from '@/hooks/useMobile';
import confetti from 'canvas-confetti';

interface Task {
  id: string;
  task_title: string;
  status: string;
  due_date: string | null;
  assignees: any[];
  checkpoints: any[];
  category_type: string;
  estimated_hours: number | null;
}

interface TaskBoardViewProps {
  tasks: Task[];
  onViewDetails: (task: Task) => void;
}

const STATUS_COLUMNS = [
  { id: 'not_started', label: 'Not Started', icon: '📋', bgColor: 'bg-slate-50' },
  { id: 'in_progress', label: 'In Progress', icon: '🏃', bgColor: 'bg-blue-50' },
  { id: 'blocked', label: 'Blocked', icon: '⛔', bgColor: 'bg-red-50' },
  { id: 'completed', label: 'Completed', icon: '✅', bgColor: 'bg-green-50' },
];

export function TaskBoardView({ tasks, onViewDetails }: TaskBoardViewProps) {
  const { toast } = useToast();
  const { user } = useAuth();
  const { isMobile } = useMobile();
  const [filterAssignee, setFilterAssignee] = useState<string>('all');
  const [mobileColumn, setMobileColumn] = useState<string>('not_started');
  
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Get unique assignees across all tasks
  const allAssignees = Array.from(
    new Map(
      tasks.flatMap(t => t.assignees || []).map((a: any) => [a.id, a])
    ).values()
  );

  // Filter tasks by assignee
  const filteredTasks = filterAssignee === 'all' 
    ? tasks 
    : filterAssignee === 'unassigned'
    ? tasks.filter(t => !t.assignees || t.assignees.length === 0)
    : tasks.filter(t => t.assignees?.some((a: any) => a.id === filterAssignee));

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;
    
    const taskId = active.id as string;
    const newStatus = over.id as string;
    
    // Don't update if dropped in the same column
    const task = tasks.find(t => t.id === taskId);
    if (task?.status === newStatus) return;

    const oldStatus = task?.status;

    try {
      const updateData: any = {
        status: newStatus,
      };

      if (newStatus === 'completed') {
        updateData.completed_at = new Date().toISOString();
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 }
        });
      } else if (task?.status === 'completed') {
        updateData.completed_at = null;
      }

      const { error } = await supabase
        .from('custom_tasks')
        .update(updateData)
        .eq('id', taskId);

      if (error) throw error;

      // Notify watchers about status change
      try {
        await supabase.functions.invoke('notify-task-status-change', {
          body: {
            taskId,
            oldStatus,
            newStatus,
            userId: user?.id
          }
        });
      } catch (notifError) {
        console.error('Error notifying watchers:', notifError);
      }

      toast({
        title: "Task Updated",
        description: `Task moved to ${STATUS_COLUMNS.find(c => c.id === newStatus)?.label}`,
      });
    } catch (error: any) {
      console.error('Error updating task:', error);
      toast({
        title: "Error",
        description: "Failed to update task status",
        variant: "destructive",
      });
    }
  };

  // Get tasks for a specific column
  const getColumnTasks = (columnId: string) => {
    return filteredTasks.filter(t => {
      let effectiveStatus = t.status;
      if (t.assignees && t.assignees.length > 0) {
        const allCompleted = t.assignees.every((a: any) => a.assignment_status === 'completed');
        const anyInProgress = t.assignees.some((a: any) => a.assignment_status === 'in_progress');
        const anyBlocked = t.assignees.some((a: any) => a.assignment_status === 'blocked');
        
        if (allCompleted) effectiveStatus = 'completed';
        else if (anyBlocked) effectiveStatus = 'blocked';
        else if (anyInProgress) effectiveStatus = 'in_progress';
      }
      return effectiveStatus === columnId;
    });
  };

  return (
    <div className="space-y-4">
      {/* Assignee Filter */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <span className="text-sm text-muted-foreground">Assigned To:</span>
        <Select value={filterAssignee} onValueChange={setFilterAssignee}>
          <SelectTrigger className="w-full sm:w-[200px] min-h-[44px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Tasks</SelectItem>
            <SelectItem value="unassigned">Unassigned ({tasks.filter(t => !t.assignees || t.assignees.length === 0).length})</SelectItem>
            {allAssignees.length > 0 && allAssignees.map((assignee: any) => {
              const taskCount = tasks.filter(t => t.assignees?.some((a: any) => a.id === assignee.id)).length;
              return (
                <SelectItem key={assignee.id} value={assignee.id}>
                  {assignee.full_name} ({taskCount})
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
        {filterAssignee !== 'all' && (
          <span className="text-xs text-muted-foreground">
            Showing {filteredTasks.length} of {tasks.length} tasks
          </span>
        )}
      </div>

      {/* Mobile: Column Selector Tabs */}
      {isMobile ? (
        <div className="space-y-4">
          <Tabs value={mobileColumn} onValueChange={setMobileColumn}>
            <TabsList className="w-full grid grid-cols-4 h-auto">
              {STATUS_COLUMNS.map((col) => (
                <TabsTrigger 
                  key={col.id} 
                  value={col.id}
                  className="text-xs px-2 py-2 min-h-[44px] flex flex-col gap-0.5"
                >
                  <span>{col.icon}</span>
                  <span className="hidden xs:inline">{col.label.split(' ')[0]}</span>
                  <span className="text-[10px] text-muted-foreground">
                    ({getColumnTasks(col.id).length})
                  </span>
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          
          <DndContext 
            onDragEnd={handleDragEnd} 
            collisionDetection={closestCorners}
            sensors={sensors}
          >
            <div className="min-h-[400px]">
              {STATUS_COLUMNS.filter(c => c.id === mobileColumn).map((column) => (
                <DroppableColumn
                  key={column.id}
                  column={column}
                  tasks={getColumnTasks(column.id)}
                  onViewDetails={onViewDetails}
                />
              ))}
            </div>
          </DndContext>
        </div>
      ) : (
        /* Desktop: All Columns */
        <DndContext 
          onDragEnd={handleDragEnd} 
          collisionDetection={closestCorners}
          sensors={sensors}
        >
          <div className="flex gap-4 overflow-x-auto pb-4 min-h-[600px]">
            {STATUS_COLUMNS.map((column) => (
              <DroppableColumn
                key={column.id}
                column={column}
                tasks={getColumnTasks(column.id)}
                onViewDetails={onViewDetails}
              />
            ))}
          </div>
        </DndContext>
      )}
    </div>
  );
}
